// 取引データ API（cells / debts / reschedules / recurring / comments / importCsv）。
// 2026-05-15 リファクタで src/api.ts から分離。
import { request } from './_request'

// === セル（資金繰りデータ） ===
export const cells = {
  /**
   * ドラッグ&ドロップ移動専用（atomic）。
   * 「ドラッグ&ドロップは支払期限を変更する」仕様。
   * - 同一vendor内: cells.date は変えず、cell_meta.due_date のみ to.displayDate に更新
   * - 異vendor間: 元 cells を soft delete、新 vendor に cells を作成（date=元の請求日を保持）+ cell_meta.due_date=to.displayDate
   *
   * fromActualDate: 元の cells.date（実日付＝請求日）。表示日付ではなく、DB上のレコード位置。
   * toDisplayDate: ドロップ先の表示日付（=新しい cell_meta.due_date）
   */
  move: (data: {
    fromVendorId: string
    fromActualDate: string
    toVendorId: string
    toDisplayDate: string
    amount: number
  }) =>
    request<{ ok: true }>('/cells?action=move', {
      method: 'POST', body: JSON.stringify(data),
    }),
  list: (from?: string, to?: string, opts?: { mine?: boolean }) => {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    if (opts?.mine) params.set('mine', '1')
    return request<{
      cells: import('../types').CellsMap
      cellMeta: import('../types').CellMetaMap
      cellUpdatedAt?: Record<string, string>
      cellUpdatedBy?: Record<string, import('../types').UserRef>
    }>(`/cells?${params}`)
  },
  save: (data: {
    cells?: Record<string, number>
    cellMeta?: Record<string, Partial<import('../types').CellMeta>>
    /** 楽観的ロック: 編集開始時の updated_at を送ると、サーバーで先行変更を検知して409 */
    expectedUpdatedAt?: Record<string, string>
  }) =>
    request('/cells', { method: 'POST', body: JSON.stringify(data) }),
  /** セルを確認済みにする（自分が入力したセルは不可） */
  confirm: (vendorId: string, date: string) =>
    request('/cell_confirm', { method: 'POST', body: JSON.stringify({ vendorId, date }) }),
  /** 確認を取り消す（確認者本人 or admin のみ） */
  unconfirm: (vendorId: string, date: string) =>
    request('/cell_confirm', { method: 'DELETE', body: JSON.stringify({ vendorId, date }) }),
}

// === 債務 ===
export const debts = {
  list: () => request<import('../types').Debt[]>('/debts'),
  listTrashed: () => request<any[]>('/debts?trashed=1'),
  create: (data: Partial<import('../types').Debt>) =>
    request('/debts', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Partial<import('../types').Debt> & { updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/debts', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/debts', { method: 'DELETE', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/debts?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
}

// === リスケジュール ===
export const reschedules = {
  list: () => request<import('../types').Reschedule[]>('/reschedules'),
  listTrashed: () => request<any[]>('/reschedules?trashed=1'),
  create: (data: Partial<import('../types').Reschedule>) =>
    request<{ id: string }>('/reschedules', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Partial<import('../types').Reschedule> & { updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/reschedules', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/reschedules', { method: 'DELETE', body: JSON.stringify({ id }) }),
  cancel: (id: string) =>
    request('/reschedules?action=cancel', { method: 'POST', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/reschedules?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
}

// === 定期取引 ===
export const recurring = {
  list: () => request<import('../types').RecurringItem[]>('/recurring'),
  listTrashed: () => request<any[]>('/recurring?trashed=1'),
  create: (data: Partial<import('../types').RecurringItem>) =>
    request('/recurring', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Partial<import('../types').RecurringItem> & { updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/recurring', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/recurring', { method: 'DELETE', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/recurring?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
  /** 月別個別金額の登録/更新（migration 025）。yearMonth は 'YYYY-MM' */
  setMonthlyAmount: (data: { recurringId: string; yearMonth: string; amount: number; note?: string }) =>
    request<{ ok: boolean }>('/recurring?type=monthly', { method: 'POST', body: JSON.stringify(data) }),
  /** 月別個別金額の削除（ベース金額に戻る） */
  removeMonthlyAmount: (data: { recurringId: string; yearMonth: string }) =>
    request<{ ok: boolean }>('/recurring?type=monthly', { method: 'DELETE', body: JSON.stringify(data) }),
}

// === コメント（取引ごとのスレッド） ===
export interface CellComment {
  id: string
  vendorId: string
  date: string
  subId: number
  userId: string
  userName: string
  body: string
  mentions: string[]
  createdAt: string
  updatedAt?: string | null
  /** 現ユーザーが既読か（自分の投稿は常にtrue） */
  isRead?: boolean
}

export interface CommentCountEntry {
  total: number
  unread: number
}

export const comments = {
  // 取引指定でコメント一覧
  list: (vendorId: string, date: string, subId: number = 0) => {
    const params = new URLSearchParams({ vendorId, date, subId: String(subId) })
    return request<{ comments: CellComment[] }>(`/comments?${params}`)
  },
  // 件数マップ（履歴一覧のバッジ用）: { "vendorId_date_subId": { total, unread } }
  counts: () => request<{ counts: Record<string, CommentCountEntry> }>('/comments?counts=1'),
  // 投稿
  create: (data: {
    vendorId: string; date: string; subId?: number;
    body: string; mentions?: string[];
  }) => request<{ ok: boolean; comment: CellComment | null }>('/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // 削除（本人 or admin）
  remove: (id: string) => request<{ ok: boolean }>('/comments', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  }),
  // 既読マーク（指定した comment id 群を既読化）
  markRead: (ids: string[]) => request<{ ok: boolean; markedRead: number }>('/comments?action=mark_read', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  }),
}

// === CSV インポート ===
export const importCsv = (data: {
  rows: Array<{ vendorId: string; date: string; amount: number; note?: string }>
  mode: 'merge' | 'overwrite'
}) => request<{ ok: boolean; total: number; created: number; updated: number; skipped: number; mode: string }>(
  '/import', { method: 'POST', body: JSON.stringify(data) }
)
